import { z } from 'zod'

/**
 * Marketing consent (sign-in-once spec §3). Opt-in everywhere (Hamza, 2026-10-13): the sign-in
 * checkbox starts unticked, and only a tick, the Settings switch or an unsubscribe changes it.
 * Consent is separate from the account and from product telemetry.
 */

/**
 * The exact consent wording, by version and locale. The service stores the version and the hash of
 * the text it names, so a client cannot claim it showed something else. A wording Bilal or Hamza
 * approves later is a new version; it never edits an old one.
 */
export const MARKETING_CONSENT_TEXTS = {
  // Draft until approved (spec §9 decision 5).
  'marketing-v1-draft': {
    en: 'Send me SignBento news and offers. You can unsubscribe at any time.',
  },
} as const satisfies Record<string, Record<string, string>>

export type MarketingConsentTextVersion = keyof typeof MARKETING_CONSENT_TEXTS

/** The version the clients show today. */
export const CURRENT_MARKETING_CONSENT_VERSION: MarketingConsentTextVersion = 'marketing-v1-draft'

/** Where a person saw the choice; provenance only, never an authority. */
export const MarketingSurface = z.enum(['web', 'ios', 'android'])
export type MarketingSurface = z.infer<typeof MarketingSurface>

/** The sign-in screen's checkbox, sent with `auth.start`. */
export const MarketingChoice = z
  .object({
    opt_in: z.boolean(),
    text_version: z.string().min(1).max(64),
    locale: z.string().min(2).max(16),
    surface: MarketingSurface,
  })
  .strict()
export type MarketingChoice = z.infer<typeof MarketingChoice>

export const MarketingConsentState = z
  .object({
    subscribed: z.boolean(),
    /** When the current decision was recorded; null when the account never decided. */
    updated_at: z.string().nullable(),
  })
  .strict()
export type MarketingConsentState = z.infer<typeof MarketingConsentState>

/** The Settings switch. */
export const MarketingConsentUpdateRequest = z
  .object({
    client_request_id: z.string().min(8).max(128),
    subscribed: z.boolean(),
    text_version: z.string().min(1).max(64),
    locale: z.string().min(2).max(16),
  })
  .strict()
export type MarketingConsentUpdateRequest = z.infer<typeof MarketingConsentUpdateRequest>

/** An unsubscribe from a marketing mail: the token is the only credential, and it can only withdraw. */
export const MarketingUnsubscribeRequest = z
  .object({
    token: z.string().min(16).max(512),
  })
  .strict()
export type MarketingUnsubscribeRequest = z.infer<typeof MarketingUnsubscribeRequest>
